/**
 * Query keys for the body-photo family (`../core/keys.ts` is the convention):
 * the consent record, the assessment history, one assessment, one photo, and
 * the personal anchor (`D16`–`D26`).
 *
 * ── THE LIST AND THE DETAIL ARE SIBLINGS UNDER ONE PREFIX ──────────────────
 * `assessmentList` is `["body","assessments","list"]` and `assessment(id)` is
 * `["body","assessments","detail",id]` — two segments under
 * {@link bodyKeys.assessments}, rather than the list's key being a prefix of
 * every item's. The catalogue's `detail` shape, for its reason: the detail
 * read carries a READ-THROUGH poll on the server (`T97`, decision 11), so a
 * write that only changed the list would otherwise spend one `bodycomp`
 * subrequest per cached pending row against a tenant limit of twenty a minute
 * shared by every account (`D26`). Invalidating both is then a deliberate act
 * (`bodyKeys.assessments`), not the accident of a prefix.
 *
 * ── A PHOTO KEY EXISTS, AND IT MUST NEVER BE PERSISTED ─────────────────────
 * `GET …/photos/:view` answers BYTES behind a bearer header, so a screen
 * fetches it and makes an object URL (see `fetchBodyAssessmentPhoto`). If that
 * goes through `useQuery` at all it MUST carry `meta: { noPersist: true }`:
 * the app's persister writes the query cache into IndexedDB, which is readable
 * by any script on the origin and survives sign-out (`query-client.ts`), and
 * what would be sitting in it is photographs of somebody's body. The key is
 * here rather than left to a screen because `../core/keys.ts` is explicit that
 * a key written by hand in a screen drifts from the one the mutation
 * invalidates — and a stale photo URL outlives a revocation.
 */
export const bodyKeys = {
    all: ["body"],
    anchor: ["body", "anchor"],
    /** The prefix over the list, every detail and every photo. */
    assessments: ["body", "assessments"],
    assessment: (id) => ["body", "assessments", "detail", id],
    assessmentList: ["body", "assessments", "list"],
    consent: ["body", "consent"],
    photo: (id, view) => ["body", "assessments", "photo", id, view],
};
/**
 * What revoking consent reaches: EVERYTHING under `["body"]`.
 *
 * A revocation deletes every photo and keeps every number (`D20`), so it flips
 * `photos.front` and `photos.side` to `false` on every row the list and the
 * details hold, invalidates every object URL a screen is showing, and clears
 * the consent record itself. There is no narrower true answer, and the anchor
 * is inside the prefix only because it is cheaper to refetch it than to write
 * a list of four keys that has to stay correct.
 */
export function bodyRevokeInvalidates() {
    return [bodyKeys.all];
}
//# sourceMappingURL=keys.js.map